import { FolderAddOutlined, FileAddOutlined } from "@ant-design/icons";
import { generatePath, useNavigate } from "react-router-dom";
import { useTranslations } from "use-intl";
import type { MenuProps } from "antd";
import { twMerge } from "tailwind-merge";
import { More } from "@/shared/actions/more";
import { FormInModal } from "@/shared/form/form-in-modal";
import {
  SaveCompanyDocumentFolderForm,
  AddCompanyDocumentFolder,
} from "@/modules/documents/features/save-company-document-folder";
import { AddCompanyDocument } from "@/modules/documents/widgets/add-company-document";
import { useDeleteCompanyDocumentFolder } from "@/modules/documents/features/delete-company-document-folder";
import { useDeleteCompanyDocumentsMutation } from "@/modules/documents/features/delete-company-documents";
import { COMPANY_DOCUMENTS_EDIT_PATH } from "@/modules/documents/pages/company-documents-edit/company-documents-edit-path";
import { TabsEnum } from "@/modules/documents/pages/company-documents-edit/company-documents-edit-tabs";
import type { CompanyDocumentTreeNode } from "@/modules/documents/widgets/company-document-tree/model/types";
import { isFolderNode } from "@/modules/documents/widgets/company-document-tree/utils/tree-node-guards";
import { valueOrNull } from "@/shared/value";

export type CompanyDocumentTreeNodeOptionsProps = {
  node: CompanyDocumentTreeNode;
};

export function CompanyDocumentTreeNodeOptions({
  node,
}: CompanyDocumentTreeNodeOptionsProps) {
  const t = useTranslations("CompanyDocumentFolder");
  const tDoc = useTranslations("Documents");
  const navigate = useNavigate();

  const { remove: removeFolder } = useDeleteCompanyDocumentFolder();
  const { remove: removeDocument } = useDeleteCompanyDocumentsMutation();

  const folder = isFolderNode(node) ? valueOrNull(node.folder) : null;
  const document = isFolderNode(node) ? null : valueOrNull(node.document);

  // Скрытые узлы редактировать и удалять нечем: у клиента нет их идентификатора.
  if (!folder && !document) {
    return null;
  }

  const items: MenuProps["items"] = folder
    ? [
        {
          key: "add-document",
          label: (
            <AddCompanyDocument
              folderId={folder.id}
              trigger={(open) => (
                <More.Button icon={<FileAddOutlined />} onClick={open}>
                  {tDoc("Add document")}
                </More.Button>
              )}
            />
          ),
        },
        {
          key: "add-folder",
          label: (
            <AddCompanyDocumentFolder
              parentId={folder.id}
              trigger={(open) => (
                <More.Button icon={<FolderAddOutlined />} onClick={open}>
                  {t("Add folder")}
                </More.Button>
              )}
            />
          ),
        },
        {
          key: "edit",
          label: (
            <FormInModal
              title={t("Edit folder")}
              trigger={(open) => (
                <More.Edit onClick={open}>{t("Edit folder")}</More.Edit>
              )}
              form={(close) => (
                <SaveCompanyDocumentFolderForm
                  id={folder.id}
                  afterSubmit={close}
                />
              )}
            />
          ),
        },
        {
          key: "delete",
          label: (
            <More.Delete
              onClick={() => removeFolder(folder.id)}
              title={t("Delete folder")}
              description={t("Delete folder confirm")}
            />
          ),
        },
      ]
    : document
      ? [
          {
            key: "edit",
            label: (
              <More.Edit
                onClick={() =>
                  navigate(
                    generatePath(COMPANY_DOCUMENTS_EDIT_PATH, {
                      documentId: document.id,
                      tab: TabsEnum.GENERAL,
                    }),
                  )
                }
              />
            ),
          },
          {
            key: "delete",
            label: <More.Delete onClick={() => removeDocument(document.id)} />,
          },
        ]
      : [];

  return (
    <More
      menu={{ items }}
      buttonProps={{
        variant: "text",
        className: twMerge(
          "!text-lg !text-[--palette-neutral-6] !shadow-none",
          "hover:!bg-transparent hover:!text-[--palette-primary-6]",
        ),
      }}
    />
  );
}
